import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Plus, Building2, Edit, X } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const DepartmentsList = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      toast.error('Błąd podczas pobierania działów');
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (department) => {
    setSelectedDepartment(department);
    setValue('name', department.name || '');
    setValue('description', department.description || '');
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedDepartment(null);
    reset();
  };

  const onEditSubmit = async (data) => {
    setSaving(true);
    try {
      await api.put(`/departments/${selectedDepartment.id}`, {
        name: data.name,
        description: data.description || null
      });
      toast.success('Dział został zaktualizowany pomyślnie');
      handleCloseModal();
      fetchDepartments(); // Odśwież listę
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas aktualizacji działu';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="text-xl">Lista działów</h2>
        </div>
        <div className="card-body flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h2 className="text-xl">Lista działów</h2>
        <button
          onClick={() => navigate('/app/departments/add')}
          className="flex items-center px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Dodaj dział
        </button>
      </div>
      <div className="card-body">
        {departments.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm text-gray-900">Brak działów</h3>
            <p className="mt-1 text-sm text-gray-500">Rozpocznij od dodania pierwszego działu.</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/app/departments/add')}
                className="inline-flex items-center px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Dodaj dział
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full overflow-x-hidden">
            <table className="w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-3 sm:px-4 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[30%]">
                    Nazwa działu
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[60%]">
                    Opis
                  </th>
                  <th className="px-3 sm:px-4 md:px-6 py-3 md:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-[10%]">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {departments.map((department) => (
                  <tr key={department.id} className="hover:bg-blue-50/30 transition-colors duration-150 border-b border-gray-100">
                    <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                      <div className="text-sm font-medium text-gray-900 flex items-center min-w-0">
                        <Building2 className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                        <span className="break-words min-w-0">{department.name}</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                      <div className="text-sm text-gray-700 break-words">
                        {department.description || <span className="text-gray-400">-</span>}
                      </div>
                    </td>
                    <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleEditClick(department)}
                        className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                        title="Edytuj dział"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal edycji działu */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edytuj dział</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit(onEditSubmit)} className="p-4 space-y-4">
              <div>
                <label htmlFor="edit-name" className="block text-sm text-gray-700 mb-2">
                  Nazwa działu <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('name', {
                    required: 'Nazwa działu jest wymagana',
                    minLength: {
                      value: 2,
                      message: 'Nazwa działu musi mieć co najmniej 2 znaki'
                    }
                  })}
                  type="text"
                  id="edit-name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="np. Administracja"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="edit-description" className="block text-sm text-gray-700 mb-2">
                  Opis działu
                </label>
                <textarea
                  {...register('description')}
                  id="edit-description"
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Opcjonalny opis działu"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {saving ? (
                    <span className="flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Zapisywanie...</span>
                    </span>
                  ) : (
                    'Zapisz zmiany'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentsList;
